/**
 * A car's monthly inspections, for its partner: /fleet/contracts/:contractId/inspections.
 *
 * One read, `get_contract_inspections`, which gives a partner the CAR's view: every visit on the
 * contract's vehicle (on all its contracts and between them), the car's open items, and this
 * contract's place in today's round (`current`). The page renders what the function derived; it
 * works out no attendance, result or hold of its own. The status card reads `current` rather than
 * the board: the two derive attendance and condition alike for a partner, and only `current`
 * carries the next window and the release, and it serves a deep link with one call.
 *
 * A page of its own, not a revival of the unrouted `VehicleDetail.tsx`, which is built around
 * repayment cards: nothing here names money. Nor the driver's phone: that is Primelane's business
 * with the driver.
 */

import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ClipboardCheck, ClipboardX } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import SectionDrawer from '@/components/navigation/SectionDrawer';
import { Breadcrumbs } from '@/components/ui/Breadcrumbs';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Skeleton } from '@/components/ui/skeleton';
import { InspectionStatusCard } from '@/components/inspections/InspectionStatusCard';
import { InspectionCertificate } from '@/components/inspections/InspectionCertificate';
import { newestVisitWithAngleShots, type ContractInspections } from '@/lib/inspections';

/**
 * What the read came back with. A refusal is an answer, not a failure: never retried, never reported.
 * For a partner, a contract id that does not exist is refused too (can_access_contract is false for
 * it, so the guard raises before the function's not-found branch), so the refused state is worded
 * to be true of both. `missing` is the function's NULL, which only an admin reaches; it is kept so
 * that answer still has a page.
 */
type InspectionsRead =
    | { kind: 'found'; data: ContractInspections }
    | { kind: 'missing' }
    | { kind: 'refused' };

/** A route param as a contract id: a positive whole number, or null. */
function parseContractId(param: string | undefined): number | null {
    if (!param || !/^\d+$/.test(param)) return null;
    const id = Number(param);
    return Number.isSafeInteger(id) && id > 0 ? id : null;
}

function isAccessDenied(error: unknown): boolean {
    return typeof error === 'object' && error !== null && (error as { message?: unknown }).message === 'Access denied';
}

const CONTRACTS_HREF = '/fleet/contracts';

export default function VehicleInspections() {
    const navigate = useNavigate();
    const { contractId: contractParam } = useParams();
    const contractId = parseContractId(contractParam);
    const { partnerId, signOut } = useAuth();

    const { data: read, isLoading, error, refetch, isFetching } = useQuery({
        queryKey: ['partner-contract-inspections', partnerId, contractId],
        queryFn: async (): Promise<InspectionsRead> => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const { data, error } = await (supabase as any).rpc('get_contract_inspections', {
                p_contract_id: contractId!,
            });
            if (error && isAccessDenied(error)) return { kind: 'refused' };
            if (error) throw error;
            const payload = (data ?? null) as ContractInspections | null;
            if (payload === null) return { kind: 'missing' };
            // The driver's view: this login drives the car but is not its partner. The function
            // admits it (can_access_contract), but it is not this app's page to show.
            if (payload.viewer === 'driver') return { kind: 'refused' };
            return { kind: 'found', data: payload };
        },
        enabled: !!partnerId && contractId !== null,
        // A function the database does not have (PGRST202) will not appear on a second ask.
        retry: (failureCount, err) => (err as { code?: unknown } | null)?.code !== 'PGRST202' && failureCount < 2,
    });

    const handleSignOut = async () => { await signOut(); navigate('/auth'); };
    const handleNavChange = (section: string) => { navigate(`/${section}`); };

    const data = read?.kind === 'found' ? read.data : null;
    const header = data?.header;
    const carName = [header?.make, header?.model].filter(Boolean).join(' ');
    const title = header?.plate?.trim() || carName || 'Inspections';
    const photosOpenOn = data ? newestVisitWithAngleShots(data.visits) : null;
    const backToContracts = <Button variant="outline" onClick={() => navigate(CONTRACTS_HREF)}>Back to contracts</Button>;

    let body: ReactNode = null;
    if (contractId === null || read?.kind === 'missing') {
        body = (
            <EmptyState
                icon={<ClipboardX />}
                title="Contract not found"
                description="There is no contract at this address. Open the car from your contracts list."
                action={backToContracts}
            />
        );
    } else if (isLoading) {
        body = (
            <div className="space-y-4">
                <Skeleton className="h-48 w-full" />
                <Skeleton className="h-40 w-full" />
                <Skeleton className="h-40 w-full" />
            </div>
        );
    } else if (read?.kind === 'refused') {
        body = (
            <EmptyState
                icon={<ClipboardX />}
                title="Not on your account"
                description="This contract is not on your account. Open the car from your contracts list."
                action={backToContracts}
            />
        );
    } else if (error) {
        body = (
            <EmptyState
                icon={<ClipboardX />}
                title="The inspections did not load"
                description="Check your connection and try again."
                action={
                    <Button variant="outline" onClick={() => refetch()} disabled={isFetching}>
                        {isFetching ? 'Trying again…' : 'Try again'}
                    </Button>
                }
            />
        );
    } else if (data) {
        body = (
            <div className="space-y-8">
                <InspectionStatusCard
                    current={data.current}
                    openItems={data.open_items}
                    checklist={data.checklist}
                    visits={data.visits}
                />

                <section className="space-y-3" aria-labelledby="inspection-history">
                    <h2 id="inspection-history" className="text-lg font-semibold tracking-tight">Inspection history</h2>
                    {data.visits.length === 0 ? (
                        <EmptyState
                            icon={<ClipboardCheck />}
                            title="No inspections yet"
                            description="Each inspection of this car will be listed here, newest first."
                            className="py-10"
                        />
                    ) : (
                        <div className="space-y-3">
                            {data.visits.map(visit => (
                                <InspectionCertificate
                                    key={visit.id}
                                    visit={visit}
                                    checklist={data.checklist}
                                    contractId={data.contract_id}
                                    defaultShowPhotos={visit.id === photosOpenOn}
                                />
                            ))}
                        </div>
                    )}
                </section>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in px-4 sm:px-6 lg:px-8 pt-6">
            {/* Mobile nav */}
            <div className="lg:hidden">
                <SectionDrawer activeSection="fleet/contracts" onSectionChange={handleNavChange} onSignOut={handleSignOut} />
            </div>

            <div className="mx-auto w-full max-w-3xl space-y-6">
                <div>
                    <Breadcrumbs items={[
                        { label: 'Contracts', href: CONTRACTS_HREF },
                        { label: 'Inspections' },
                    ]} />
                    {isLoading ? (
                        <Skeleton className="h-9 w-48" />
                    ) : (
                        <h1 className="text-3xl font-semibold tracking-tight">{title}</h1>
                    )}
                    {data && (
                        <p className="mt-1 text-muted-foreground">
                            {[header?.plate?.trim() ? carName : null, `Contract #${data.contract_id}`].filter(Boolean).join(' · ')}
                        </p>
                    )}
                </div>

                {body}
            </div>
        </div>
    );
}
